import { useTheme } from '@emotion/react'
import styled from '@emotion/styled'

import { iconIdMapping } from '@arcade/web/icons/react'
import { IconMat } from '@cash-design-system/react'
import { CellDefaultCellDefaultIconTintedIcon } from '@cash-web/protos/squareup/cash/ui/arcade/elements/CellDefault.pb'
import logger from '@cash-web/shared/util-logger'

// Yeah, this mapping is hacky and kinda sucks. But in lieu of changing our design system to
// accept custom colors, this is what we've gotta do.
const hexToVariantMap: Record<
  'dark' | 'light',
  Record<string, NonNullable<React.ComponentProps<typeof IconMat>['variant']>>
> = {
  dark: {
    '#1A1A1A': 'subtle',
  },
  light: {
    '#F0F0F0': 'subtle',
  },
}

const Color = styled.div<{ color: string }>`
  color: ${({ color, theme }) => color};
  display: flex;
  align-items: center;
  justify-content: center;
`

export function TintedArcadeCellIcon({
  tintedIcon,
  isBackgroundRequired = true,
}: {
  tintedIcon: CellDefaultCellDefaultIconTintedIcon
  isBackgroundRequired?: boolean
}) {
  const { darkMode, arcadeColors } = useTheme()
  const iconArcadeId = tintedIcon.icon?.arcade_id
  const icon = iconArcadeId ? iconIdMapping[iconArcadeId] : undefined

  if (!icon) {
    // If you run into this error it either means we need to update our mapping, or the server
    // accidentally provided an invalid arcade id.
    logger.warn(new Error(`ArcadeCellIconFromProto: iconArcadeId is missing from mapping: ${iconArcadeId}`))
    return null
  }

  const lightBackgroundHex = tintedIcon.background_color?.light?.srgb
  const darkBackgroundHex = tintedIcon.background_color?.dark?.srgb
  const backgroundHex = darkMode ? darkBackgroundHex ?? lightBackgroundHex : lightBackgroundHex

  const lightTintColor = tintedIcon.icon_tint_color?.light?.srgb
  const darkTintColor = tintedIcon.icon_tint_color?.dark?.srgb
  const tintColor = darkMode ? darkTintColor ?? lightTintColor : lightTintColor

  if (!backgroundHex && isBackgroundRequired) {
    // If you run into this error, the response needs to be fixed to include a background color.
    logger.error(new Error('ArcadeCellIconFromProto: background_color is missing from server response'))
    return null
  }

  const defaultIconTintColor = isBackgroundRequired
    ? arcadeColors.semantic.iconInverse
    : arcadeColors.semantic.iconStandard

  const coloredIcon = (
    <Color color={tintColor ?? defaultIconTintColor}>
      <icon.Icon size={icon.size} />
    </Color>
  )

  if (backgroundHex) {
    const iconMatVariant = darkMode ? hexToVariantMap.dark[backgroundHex] : hexToVariantMap.light[backgroundHex]
    if (!iconMatVariant) {
      // If you run into this error, we need to either update our hexToVariantMap to account for more variants or
      // we need to update the design system to allow us to pass in custom hex codes to IconMat.
      logger.warn(new Error(`ArcadeCellIconFromProto: unknown iconMatVariant for background_color: ${backgroundHex}`))
    }
    return (
      <IconMat variant={iconMatVariant} size="small">
        {coloredIcon}
      </IconMat>
    )
  }

  return coloredIcon
}
